import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { 
  Alert, 
  Autocomplete, 
  Box, 
  Button, 
  CircularProgress, 
  Stack, 
  TextField, 
  Typography,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { User } from '@/types/v2/user';
import axios from '@/lib/v2/axios';
import { ProfessionalDialog } from '@/components/common';
import { getCommunityManagers } from '@/api/communities';
import { UserPlus, MagnifyingGlass, User as UserIcon } from '@phosphor-icons/react';

interface AssignOrCreateManagerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  onCreateNew: () => void;
  communityId: string;
}

export function AssignOrCreateManagerModal({
  open,
  onClose,
  onSuccess,
  onCreateNew,
  communityId,
}: AssignOrCreateManagerModalProps): React.JSX.Element {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [managerIds, setManagerIds] = useState<Set<string>>(new Set());
  const [autoOpen, setAutoOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');

  // Managers in this community (for filtering)
  const managersQuery = useQuery({
    queryKey: ['community-managers', communityId],
    queryFn: async () => getCommunityManagers(communityId),
    enabled: open && Boolean(communityId),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (managersQuery.data?.managers) {
      setManagerIds(new Set((managersQuery.data.managers).map((m: User) => String(m.id))));
    }
  }, [managersQuery.data]);

  // Eligible users (community residents excluding account admins and current community managers)
  const usersQuery = useQuery({
    queryKey: ['eligible-residents', { q: inputValue || '', communityId }],
    queryFn: async () => {
      await axios.get('/sanctum/csrf-cookie');
      const params: Record<string, string> = { include: 'accountRoles,roles,communityRoles' } as Record<string, string>;
      if (inputValue) params['filter[email]'] = inputValue;
      const response = await axios.get(`/api/v2/communities/${communityId}/residents`, { params });
      return response.data;
    },
    enabled: open && autoOpen,
    staleTime: 30_000,
  });

  const options: User[] = React.useMemo(() => {
    const raw: any = usersQuery.data;
    const users: User[] = raw?.residents ?? raw?.data ?? [];
    return users.filter((u: any) => {
      const hasAccountAdmin = (u.account_roles || []).some((r: any) => r.name === 'account_admin');
      const isCurrentManager = managerIds.has(String(u.id));
      // Check if user has any community manager role from both roles and communityRoles
      const hasCommunityManagerRole = (u.roles || []).some((r: any) => r?.name === 'community_manager') ||
                                     (u.community_roles || []).some((r: any) => r?.name === 'community_manager');
      return !hasAccountAdmin && !isCurrentManager && !hasCommunityManagerRole;
    });
  }, [usersQuery.data, managerIds, communityId]);

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setError(null);
      setManagerIds(new Set());
      setAutoOpen(false);
      setInputValue('');
    }
  }, [open]);

  const assignMutation = useMutation({
    mutationFn: async (userId: string) => {
      await axios.get('/sanctum/csrf-cookie');
      // Use the selectedUser directly to ensure we have all the data
      if (!selectedUser) {
        throw new Error('No user selected');
      }
      
      return axios.post(`/api/v2/communities/${communityId}/managers`, {
        email: selectedUser.email || '',
        first_name: selectedUser.first_name || 'N/A',
        last_name: selectedUser.last_name || 'N/A',
        send_invite: false,
      });
    },
  });

  const handleAssign = useCallback(async () => {
    if (!selectedUser) return;
    
    // Validate that the selected user has required fields
    if (!selectedUser.email) {
      setError('Selected user must have an email address');
      return;
    }
    
    try {
      const response = await assignMutation.mutateAsync(selectedUser.id);
      toast.success('Assigned as manager', { position: 'top-right' });
      onSuccess(response?.data);
      onClose(); // Close the dialog on success
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.message || 'Failed to assign as manager');
    }
  }, [selectedUser, assignMutation, onSuccess, onClose]);

  const renderUserOption = (props: any, option: User) => (
    <Box component="li" {...props} sx={{ py: 1 }}>
      <Stack alignItems="center" direction="row" spacing={2} width="100%">
        <Avatar 
          sx={{ 
            width: 32, 
            height: 32, 
            bgcolor: 'primary.main',
            fontSize: '0.875rem'
          }}
        >
          {option.first_name?.[0] || option.email?.[0] || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 500, color: 'text.primary' }} variant="body2">
            {option.first_name && option.last_name 
              ? `${option.first_name} ${option.last_name}`
              : option.first_name || option.last_name || 'No Name'
            }
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="caption">
            {option.email}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );

  return (
    <ProfessionalDialog
      actions={
        <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
          <Button onClick={onClose} variant="outlined">Cancel</Button>
          <LoadingButton
            disabled={!selectedUser || assignMutation.isPending}
            loading={assignMutation.isPending}
            onClick={handleAssign}
            variant="contained"
          >
            Assign
          </LoadingButton>
        </Box>
      }
      contentSx={{ display: 'flex', flexDirection: 'column', gap: 3, minHeight: 0, py: 2 }}
      icon={<UserPlus size={24} />}
      maxWidth="sm"
      onClose={onClose}
      open={open}
      subtitle="Search for an existing user to assign as a community manager. To add a new user, create them first." 
      title="Assign a Manager"
      variant="modern"
    >
      {error ? <Alert severity="error">{error}</Alert> : null}
      
      <Box>
        <Typography sx={{ mb: 2, color: 'text.secondary' }} variant="body2">
          Search for users by name or email address
        </Typography>
        
        <Autocomplete
          getOptionLabel={(option) => option.email || `${option.first_name ?? ''} ${option.last_name ?? ''}`.trim()}
          inputValue={inputValue}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={usersQuery.isFetching ? autoOpen : null}
          loadingText="Loading users..."
          noOptionsText={inputValue ? 'No users found. Create a user first.' : 'Type to search users'}
          onChange={(_e, value) => { setSelectedUser(value); }}
          onClose={() => { setAutoOpen(false); }}
          onInputChange={(_e, value) => { setInputValue(value); }}
          onOpen={() => { setAutoOpen(true); }}
          open={autoOpen}
          options={options}
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <Box sx={{ mr: 1, color: 'text.secondary' }}>
                    <MagnifyingGlass size={20} />
                  </Box>
                ),
                endAdornment: (
                  <React.Fragment>
                    {usersQuery.isFetching && autoOpen ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              label="Search users"
              placeholder="Type name or email..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          )}
          renderOption={renderUserOption}
          sx={{
            '& .MuiAutocomplete-listbox': {
              py: 0,
            },
          }}
        />
      </Box>

      {selectedUser ? <Box sx={{ 
          p: 2, 
          bgcolor: 'background.default', 
          borderRadius: 2, 
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Stack alignItems="center" direction="row" spacing={2}>
            <Avatar 
              sx={{ 
                width: 48, 
                height: 48, 
                bgcolor: 'primary.main',
                fontSize: '1.125rem'
              }}
            >
              {selectedUser.first_name?.[0] || selectedUser.email?.[0] || 'U'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="h6">
                {selectedUser.first_name && selectedUser.last_name 
                  ? `${selectedUser.first_name} ${selectedUser.last_name}`
                  : selectedUser.first_name || selectedUser.last_name || 'No Name'
                }
              </Typography>
              <Typography sx={{ color: 'text.secondary' }} variant="body2">
                {selectedUser.email}
              </Typography>
              <Chip 
                color="success" 
                label="Ready to assign" 
                size="small" 
                sx={{ mt: 1 }}
              />
            </Box>
          </Stack>
        </Box> : null}

      <Divider />
      
      <Box sx={{ 
        p: 2, 
        bgcolor: 'background.paper', 
        borderRadius: 2, 
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Stack alignItems="center" direction="row" spacing={2}>
          <Box sx={{ 
            p: 1, 
            bgcolor: 'info.light', 
            borderRadius: 1,
            color: 'info.contrastText'
          }}>
            <UserIcon size={20} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 500, color: 'text.primary' }} variant="body2">
              Can't find the user?
            </Typography>
            <Typography sx={{ color: 'text.secondary' }} variant="caption">
              Create them first, then assign as manager.
            </Typography>
          </Box>
        </Stack>
      </Box>
    </ProfessionalDialog>
  );
}


